import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Button,
    Grid,
    Typography,
    CircularProgress,
    Box,
    Chip,
    Divider,
    Alert,
    Card,
    CardContent,
    CardActions,
    Collapse,
    IconButton,
    LinearProgress,
    Tooltip,
    Accordion,
    AccordionSummary,
    AccordionDetails,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    InputAdornment
} from '@mui/material';
import { makeStyles } from '@mui/styles';
import Modal from '@mui/material/Modal';
import Backdrop from '@mui/material/Backdrop';
import Fade from '@mui/material/Fade';
import SendIcon from '@mui/icons-material/Send';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import LockIcon from '@mui/icons-material/Lock';
import CloseIcon from '@mui/icons-material/Close';
import { injectIntl } from 'react-intl';
import * as WebhookService from '../../services/webhook.service';
import JsonEditor from '../json-editor';

const AUTH_TYPES = [
    { value: 'NONE', labelId: 'webhook.auth.none' },
    { value: 'BASIC', labelId: 'webhook.auth.basic' },
    { value: 'BEARER', labelId: 'webhook.auth.bearer' },
    { value: 'API_KEY', labelId: 'webhook.auth.apikey' }
];

// Helper para acessar campos case-insensitive
const getField = (obj, ...fields) => {
    if (!obj) return undefined;
    for (const field of fields) {
        if (obj[field] !== undefined) return obj[field];
        if (obj[field.toLowerCase()] !== undefined) return obj[field.toLowerCase()];
        if (obj[field.toUpperCase()] !== undefined) return obj[field.toUpperCase()];
    }
    return undefined;
};

// Payloads de exemplo completos e realistas por tipo de evento
// Seguindo o padrão CloudEvents usado pelo backend
const getExamplePayload = (eventCode) => {
    const basePayload = {
        id: "550e8400-e29b-41d4-a716-446655440000",
        type: `mexx:event:${eventCode}`,
        version: "1.0",
        time: new Date().toISOString(),
        source: "mexx.platform",
        hirer_id: 100
    };

    const examples = {
        // ==================== CHAMADOS ====================
        'called.created': {
            ...basePayload,
            subject: "mexx:called:12345",
            data: {
                called: {
                    id: 12345,
                    guid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    title: "Problema com impressora do setor financeiro",
                    description: "A impressora HP LaserJet do setor financeiro não está imprimindo. Aparece mensagem de erro 'Falha de comunicação'. Já foi reiniciada mas o problema persiste.",
                    opening_date: new Date().toISOString(),
                    due_date: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(),
                    status: { id: 1, name: "Aberto", color: "#4CAF50" },
                    priority: { id: 2, name: "Alta", sla_hours: 8 },
                    type: { id: 5, name: "Incidente", category: "Hardware" },
                    category: { id: 10, name: "Impressoras", parent_id: 5 },
                    department: { id: 3, name: "Financeiro", cost_center: "FIN001" },
                    location: { id: 1, name: "Matriz", address: "Av. Principal, 1000" },
                    requesting_user: {
                        id: 200,
                        name: "Maria Santos",
                        email: "maria.santos@empresa.com",
                        phone: "(11) 99999-1234",
                        department: "Financeiro",
                        position: "Analista Financeiro"
                    },
                    assigned_user: null,
                    assigned_group: { id: 2, name: "Suporte N1" },
                    custom_fields: {
                        patrimonio: "PAT-2024-0456",
                        modelo_equipamento: "HP LaserJet Pro M404n",
                        numero_serie: "VNB3K12345"
                    },
                    attachments: [
                        { id: 1, name: "foto_erro.jpg", size: 245760, mime_type: "image/jpeg" }
                    ],
                    sla: {
                        first_response_deadline: new Date(Date.now() + 2 * 60 * 60 * 1000).toISOString(),
                        resolution_deadline: new Date(Date.now() + 8 * 60 * 60 * 1000).toISOString(),
                        is_breached: false
                    }
                }
            }
        },
        'called.status_changed': {
            ...basePayload,
            subject: "mexx:called:12345",
            data: {
                called: {
                    id: 12345,
                    guid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    title: "Problema com impressora do setor financeiro",
                    status: { id: 2, name: "Em Atendimento", color: "#2196F3" },
                    assigned_user: {
                        id: 150,
                        name: "João Silva",
                        email: "joao.silva@empresa.com"
                    },
                    updated_at: new Date().toISOString()
                }
            },
            changes: {
                status_id: {
                    previous: { id: 1, name: "Aberto" },
                    current: { id: 2, name: "Em Atendimento" }
                },
                assigned_user_id: {
                    previous: null,
                    current: { id: 150, name: "João Silva" }
                }
            }
        },
        'called.updated': {
            ...basePayload,
            subject: "mexx:called:12345",
            data: {
                called: {
                    id: 12345,
                    guid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    title: "Problema com impressora do setor financeiro - ATUALIZADO",
                    description: "Descrição atualizada com mais detalhes do problema...",
                    status: { id: 2, name: "Em Atendimento", color: "#2196F3" },
                    priority: { id: 3, name: "Crítica", sla_hours: 4 },
                    assigned_user: {
                        id: 150,
                        name: "João Silva",
                        email: "joao.silva@empresa.com"
                    },
                    updated_at: new Date().toISOString(),
                    updated_by: { id: 1, name: "Admin Sistema" }
                }
            },
            changes: {
                title: {
                    previous: "Problema com impressora do setor financeiro",
                    current: "Problema com impressora do setor financeiro - ATUALIZADO"
                },
                priority_id: {
                    previous: { id: 2, name: "Alta" },
                    current: { id: 3, name: "Crítica" }
                }
            }
        },
        'called.closed': {
            ...basePayload,
            subject: "mexx:called:12345",
            data: {
                called: {
                    id: 12345,
                    guid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    title: "Problema com impressora do setor financeiro",
                    status: { id: 5, name: "Finalizado", color: "#9E9E9E" },
                    resolution: {
                        description: "Impressora foi substituída por equipamento reserva. Equipamento original enviado para manutenção externa.",
                        category: { id: 3, name: "Substituição de equipamento" },
                        resolved_by: { id: 150, name: "João Silva" },
                        resolved_at: new Date().toISOString()
                    },
                    metrics: {
                        first_response_time_minutes: 45,
                        resolution_time_minutes: 180,
                        sla_met: true,
                        customer_satisfaction_score: null
                    },
                    closed_at: new Date().toISOString(),
                    closed_by: { id: 150, name: "João Silva" }
                }
            }
        },
        'called.cancelled': {
            ...basePayload,
            subject: "mexx:called:12345",
            data: {
                called: {
                    id: 12345,
                    guid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    title: "Problema com impressora do setor financeiro",
                    status: { id: 6, name: "Cancelado", color: "#F44336" },
                    cancellation: {
                        reason: "Solicitante informou que o problema foi resolvido pelo próprio setor.",
                        category: { id: 1, name: "Resolvido pelo usuário" },
                        cancelled_by: { id: 200, name: "Maria Santos" },
                        cancelled_at: new Date().toISOString()
                    }
                }
            }
        },
        'called.assigned': {
            ...basePayload,
            subject: "mexx:called:12345",
            data: {
                called: {
                    id: 12345,
                    guid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    title: "Problema com impressora do setor financeiro",
                    status: { id: 2, name: "Em Atendimento" }
                },
                assignment: {
                    previous_user: null,
                    current_user: {
                        id: 150,
                        name: "João Silva",
                        email: "joao.silva@empresa.com",
                        group: { id: 2, name: "Suporte N1" }
                    },
                    assigned_by: { id: 1, name: "Sistema" },
                    assigned_at: new Date().toISOString(),
                    assignment_type: "automatic"
                }
            }
        },
        'called.sla_breached': {
            ...basePayload,
            subject: "mexx:called:12345",
            data: {
                called: {
                    id: 12345,
                    guid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    title: "Problema com impressora do setor financeiro",
                    status: { id: 2, name: "Em Atendimento" },
                    priority: { id: 3, name: "Crítica" }
                },
                sla_breach: {
                    breach_type: "resolution",
                    deadline: new Date(Date.now() - 60 * 60 * 1000).toISOString(),
                    breached_at: new Date().toISOString(),
                    time_exceeded_minutes: 60,
                    assigned_user: {
                        id: 150,
                        name: "João Silva",
                        email: "joao.silva@empresa.com"
                    }
                }
            }
        },
        'called.comment_added': {
            ...basePayload,
            subject: "mexx:called:12345",
            data: {
                called: {
                    id: 12345,
                    guid: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    title: "Problema com impressora do setor financeiro"
                },
                comment: {
                    id: 5678,
                    content: "Realizei o diagnóstico inicial. O problema parece ser na placa de rede da impressora. Vou solicitar peça de reposição.",
                    is_internal: false,
                    created_by: {
                        id: 150,
                        name: "João Silva",
                        type: "agent"
                    },
                    created_at: new Date().toISOString(),
                    attachments: []
                }
            }
        },

        // ==================== CHAT / MENSAGERIA ====================
        'chat.message_added': {
            ...basePayload,
            subject: "mexx:chat:9876",
            data: {
                chat: {
                    id: 9876,
                    guid: "c3d4e5f6-a7b8-9012-cdef-g34567890123",
                    title: "Atendimento via WhatsApp",
                    channel: "whatsapp",
                    status: "active",
                    customer: {
                        id: 300,
                        name: "Carlos Oliveira",
                        phone: "+5511987654321",
                        email: "carlos.oliveira@gmail.com"
                    },
                    assigned_agent: {
                        id: 150,
                        name: "João Silva"
                    }
                },
                message: {
                    id: 55555,
                    content: "Olá, preciso de ajuda com meu pedido #12345. Ainda não recebi e já passou do prazo.",
                    message_type: "TEXT",
                    direction: "inbound",
                    sender: {
                        name: "Carlos Oliveira",
                        type: "CUSTOMER",
                        phone: "+5511987654321"
                    },
                    sent_at: new Date().toISOString(),
                    delivered_at: new Date().toISOString(),
                    read_at: null,
                    metadata: {
                        whatsapp_message_id: "wamid.abcdef123456"
                    }
                }
            }
        },
        'chat.started': {
            ...basePayload,
            subject: "mexx:chat:9876",
            data: {
                chat: {
                    id: 9876,
                    guid: "c3d4e5f6-a7b8-9012-cdef-g34567890123",
                    title: "Atendimento via WhatsApp",
                    channel: "whatsapp",
                    status: "waiting",
                    queue: { id: 1, name: "Atendimento Geral" },
                    customer: {
                        id: 300,
                        name: "Carlos Oliveira",
                        phone: "+5511987654321",
                        email: "carlos.oliveira@gmail.com",
                        is_new: false,
                        previous_chats_count: 3
                    },
                    started_at: new Date().toISOString(),
                    initial_message: "Olá, preciso de ajuda"
                }
            }
        },
        'chat.ended': {
            ...basePayload,
            subject: "mexx:chat:9876",
            data: {
                chat: {
                    id: 9876,
                    guid: "c3d4e5f6-a7b8-9012-cdef-g34567890123",
                    title: "Atendimento via WhatsApp",
                    channel: "whatsapp",
                    status: "closed",
                    customer: {
                        id: 300,
                        name: "Carlos Oliveira"
                    },
                    assigned_agent: {
                        id: 150,
                        name: "João Silva"
                    },
                    started_at: new Date(Date.now() - 30 * 60 * 1000).toISOString(),
                    ended_at: new Date().toISOString(),
                    ended_by: "agent",
                    metrics: {
                        duration_minutes: 30,
                        messages_count: 12,
                        first_response_time_seconds: 45,
                        average_response_time_seconds: 120
                    },
                    resolution: {
                        category: { id: 5, name: "Problema resolvido" },
                        notes: "Cliente orientado sobre status do pedido."
                    }
                }
            }
        },
        'chat.transferred': {
            ...basePayload,
            subject: "mexx:chat:9876",
            data: {
                chat: {
                    id: 9876,
                    guid: "c3d4e5f6-a7b8-9012-cdef-g34567890123",
                    title: "Atendimento via WhatsApp",
                    status: "active"
                },
                transfer: {
                    from_agent: { id: 150, name: "João Silva" },
                    to_agent: { id: 160, name: "Ana Costa" },
                    to_queue: null,
                    reason: "Especialização em logística necessária",
                    transferred_at: new Date().toISOString(),
                    transferred_by: { id: 150, name: "João Silva" }
                }
            }
        },

        // ==================== USUÁRIOS ====================
        'user.created': {
            ...basePayload,
            subject: "mexx:user:500",
            data: {
                user: {
                    id: 500,
                    guid: "d4e5f6a7-b8c9-0123-defg-h45678901234",
                    name: "Pedro Souza",
                    email: "pedro.souza@empresa.com",
                    document: "123.456.789-00",
                    phone: "(11) 98765-4321",
                    department: { id: 5, name: "Comercial" },
                    position: "Executivo de Vendas",
                    profile: { id: 3, name: "Usuário Padrão" },
                    groups: [
                        { id: 10, name: "Equipe Comercial SP" }
                    ],
                    is_active: true,
                    created_at: new Date().toISOString(),
                    created_by: { id: 1, name: "Admin Sistema" }
                }
            }
        },
        'user.updated': {
            ...basePayload,
            subject: "mexx:user:500",
            data: {
                user: {
                    id: 500,
                    guid: "d4e5f6a7-b8c9-0123-defg-h45678901234",
                    name: "Pedro Souza",
                    email: "pedro.souza@empresa.com",
                    department: { id: 6, name: "Pós-Vendas" },
                    position: "Gerente de Contas",
                    updated_at: new Date().toISOString(),
                    updated_by: { id: 1, name: "Admin Sistema" }
                }
            },
            changes: {
                department_id: {
                    previous: { id: 5, name: "Comercial" },
                    current: { id: 6, name: "Pós-Vendas" }
                },
                position: {
                    previous: "Executivo de Vendas",
                    current: "Gerente de Contas"
                }
            }
        },
        'user.deactivated': {
            ...basePayload,
            subject: "mexx:user:500",
            data: {
                user: {
                    id: 500,
                    guid: "d4e5f6a7-b8c9-0123-defg-h45678901234",
                    name: "Pedro Souza",
                    email: "pedro.souza@empresa.com",
                    is_active: false,
                    deactivated_at: new Date().toISOString(),
                    deactivated_by: { id: 1, name: "Admin Sistema" },
                    deactivation_reason: "Desligamento da empresa"
                }
            }
        },
        'user.password_changed': {
            ...basePayload,
            subject: "mexx:user:500",
            data: {
                user: {
                    id: 500,
                    guid: "d4e5f6a7-b8c9-0123-defg-h45678901234",
                    name: "Pedro Souza",
                    email: "pedro.souza@empresa.com"
                },
                password_change: {
                    changed_at: new Date().toISOString(),
                    changed_by: "self",
                    ip_address: "192.168.1.100",
                    user_agent: "Mozilla/5.0 (Windows NT 10.0; Win64; x64)"
                }
            }
        }
    };

    // Retornar payload do evento ou um payload genérico
    return examples[eventCode] || {
        ...basePayload,
        data: {
            event: eventCode,
            message: "Este é um payload de exemplo para o evento.",
            timestamp: new Date().toISOString()
        }
    };
};

const useStyles = makeStyles(theme => ({
    modal: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
    },
    paper: {
        backgroundColor: theme.palette.background.paper,
        border: '0px solid #ffffff',
        boxShadow: theme.shadows[5],
        padding: theme.spacing(3, 4, 3),
        zIndex: 1301,
        maxWidth: '900px',
        width: '95%',
        maxHeight: '90vh',
        overflow: 'auto',
        borderRadius: '8px'
    },
    eventCard: {
        marginBottom: theme.spacing(2),
        border: '1px solid #e0e0e0'
    },
    eventCardSuccess: {
        borderColor: '#4caf50',
        borderWidth: 2
    },
    eventCardError: {
        borderColor: '#f44336',
        borderWidth: 2
    },
    eventCardPending: {
        borderColor: '#ff9800',
        borderWidth: 2
    },
    resultBox: {
        marginTop: theme.spacing(1),
        padding: theme.spacing(1),
        backgroundColor: '#f5f5f5',
        borderRadius: '4px',
        fontFamily: 'monospace',
        fontSize: '11px',
        maxHeight: '150px',
        overflow: 'auto'
    },
    successChip: {
        backgroundColor: '#4caf50',
        color: 'white'
    },
    errorChip: {
        backgroundColor: '#f44336',
        color: 'white'
    },
    pendingChip: {
        backgroundColor: '#ff9800',
        color: 'white'
    }
}));

function WebhookTestModal(props) {
    const classes = useStyles();
    const {
        isOpen,
        handleClose,
        webhookId,
        webhookConfig,
        webhookName,
        eventTypes,
        selectedEventIds,
        subscriptions,
        endpointUrl: propEndpointUrl,
        authType: propAuthType,
        authConfig: propAuthConfig,
        hasAuthConfig: propHasAuthConfig,
        customHeaders: propCustomHeaders,
        intl
    } = props;

    const [loading, setLoading] = useState(false);
    const [testingAll, setTestingAll] = useState(false);
    const [eventPayloads, setEventPayloads] = useState({});
    const [testResults, setTestResults] = useState({});
    const [expandedEvents, setExpandedEvents] = useState({});
    const [progress, setProgress] = useState(0);

    // Estado para configurações de autenticação do teste
    const [testAuthType, setTestAuthType] = useState('NONE');
    const [testAuthConfig, setTestAuthConfig] = useState({});
    const [authExpanded, setAuthExpanded] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showToken, setShowToken] = useState(false);

    // Extrair e filtrar eventos selecionados da estrutura agrupada da API
    const selectedEvents = useMemo(() => {
        if (!eventTypes || !selectedEventIds || selectedEventIds.length === 0) return [];

        const allEvents = [];

        // Verificar se vem agrupado (formato EventTypeCategoryDto)
        const firstItem = eventTypes[0];
        const hasEventTypes = firstItem && (firstItem.eventTypes || firstItem.eventtypes);

        if (hasEventTypes) {
            // Formato agrupado por categoria
            eventTypes.forEach(categoryGroup => {
                const events = getField(categoryGroup, 'eventTypes', 'eventtypes') || [];
                events.forEach(event => {
                    const eventId = getField(event, 'id', 'Id');
                    if (eventId && selectedEventIds.includes(eventId)) {
                        allEvents.push({
                            id: eventId,
                            eventCode: getField(event, 'eventCode', 'eventcode', 'EventCode') || '',
                            description: getField(event, 'description', 'eventDescription', 'eventdescription', 'Description') || ''
                        });
                    }
                });
            });
        } else {
            // Formato lista plana
            eventTypes.forEach(event => {
                const eventId = getField(event, 'id', 'Id');
                if (eventId && selectedEventIds.includes(eventId)) {
                    allEvents.push({
                        id: eventId,
                        eventCode: getField(event, 'eventCode', 'eventcode', 'EventCode') || '',
                        description: getField(event, 'description', 'eventDescription', 'eventdescription', 'Description') || ''
                    });
                }
            });
        }

        return allEvents;
    }, [eventTypes, selectedEventIds]);

    // Helper para obter a subscription de um evento pelo eventTypeId
    const getSubscriptionForEvent = useCallback((eventId) => {
        if (!subscriptions || subscriptions.length === 0) return null;
        return subscriptions.find(sub => {
            const subEventTypeId = sub.eventTypeId || sub.EventTypeId || sub.eventtypeid;
            return subEventTypeId === eventId;
        });
    }, [subscriptions]);

    // Helper para obter o payload baseado no formato configurado
    const getPayloadForEvent = useCallback((event) => {
        const subscription = getSubscriptionForEvent(event.id);

        if (!subscription) {
            // Sem subscription, usar payload padrão
            return JSON.stringify(getExamplePayload(event.eventCode), null, 2);
        }

        const payloadFormat = subscription.payloadFormat || subscription.payloadformat || 'STANDARD';
        const customTemplate = subscription.customPayloadTemplate || subscription.custompayloadtemplate;

        if (payloadFormat === 'CUSTOM_JSON' && customTemplate) {
            // Usar o template JSON customizado
            try {
                // Tentar formatar se for JSON válido
                const parsed = JSON.parse(customTemplate);
                return JSON.stringify(parsed, null, 2);
            } catch {
                // Se não for JSON válido, retornar como está
                return customTemplate;
            }
        } else if (payloadFormat === 'CUSTOM_FORM' && customTemplate) {
            // Para FORM_DATA, mostrar o array de campos como JSON
            try {
                const formItems = JSON.parse(customTemplate);
                // Converter array de {key, value} para objeto para melhor visualização
                const formObject = {};
                if (Array.isArray(formItems)) {
                    formItems.forEach(item => {
                        if (item.key) {
                            formObject[item.key] = item.value || '';
                        }
                    });
                }
                return JSON.stringify({
                    _format: 'FORM_DATA (application/x-www-form-urlencoded)',
                    _note: 'Os campos abaixo serão enviados como form-data',
                    fields: formObject
                }, null, 2);
            } catch {
                return customTemplate;
            }
        } else {
            // STANDARD - usar payload padrão do Mexx
            return JSON.stringify(getExamplePayload(event.eventCode), null, 2);
        }
    }, [getSubscriptionForEvent]);

    // Inicializar payloads quando o modal abre
    useEffect(() => {
        if (isOpen && selectedEvents && selectedEvents.length > 0) {
            const payloads = {};
            const expanded = {};
            selectedEvents.forEach(event => {
                const eventCode = event.eventCode;
                if (eventCode) {
                    payloads[eventCode] = getPayloadForEvent(event);
                    expanded[eventCode] = true; // Todos expandidos por padrão
                }
            });
            setEventPayloads(payloads);
            setExpandedEvents(expanded);
            setTestResults({});
            setProgress(0);
        }
    }, [isOpen, selectedEvents, getPayloadForEvent]);

    // Inicializar configuração de autenticação quando o modal abre
    useEffect(() => {
        if (isOpen) {
            // Carregar auth config do webhook se existir
            if (propAuthType && propAuthType !== 'NONE') {
                setTestAuthType(propAuthType);
                // O backend retorna campos não sensíveis preenchidos (username, headerName, clientId, tokenUrl, scope)
                // Mas campos sensíveis (password, token, apiKey, clientSecret) vêm vazios
                if (propAuthConfig && Object.keys(propAuthConfig).length > 0 && !propAuthConfig._hasConfig) {
                    // Temos dados parciais da API - usar como base
                    setTestAuthConfig(propAuthConfig);
                    // Expandir para o usuário ver e preencher os campos sensíveis faltantes
                    setAuthExpanded(true);
                } else if (propHasAuthConfig) {
                    // Tem auth configurado mas não recebemos dados - inicializar vazio
                    setTestAuthConfig({});
                    setAuthExpanded(true);
                } else {
                    setTestAuthConfig({});
                }
            } else {
                setTestAuthType('NONE');
                setTestAuthConfig({});
            }
        }
    }, [isOpen, propAuthType, propAuthConfig, propHasAuthConfig]);

    const handlePayloadChange = (eventCode, value) => {
        setEventPayloads(prev => ({
            ...prev,
            [eventCode]: value
        }));
    };

    const toggleExpand = (eventCode) => {
        setExpandedEvents(prev => ({
            ...prev,
            [eventCode]: !prev[eventCode]
        }));
    };

    const testSingleEvent = async (eventCode) => {
        setTestResults(prev => ({
            ...prev,
            [eventCode]: { loading: true }
        }));

        try {
            let payloadStr = eventPayloads[eventCode];
            // Validar se é JSON válido antes de enviar
            if (payloadStr && payloadStr.trim()) {
                try {
                    JSON.parse(payloadStr); // Apenas validar, não usar o resultado
                } catch (e) {
                    setTestResults(prev => ({
                        ...prev,
                        [eventCode]: {
                            success: false,
                            errorMessage: intl.formatMessage({ id: 'webhook.test.invalid.json' })
                        }
                    }));
                    return false;
                }
            } else {
                payloadStr = null;
            }

            let result;
            if (webhookId) {
                // Construir config com autenticação do modal
                const testConfig = {
                    endpointUrl: propEndpointUrl,
                    httpMethod: 'POST',
                    authType: testAuthType,
                    authConfig: testAuthType !== 'NONE' ? testAuthConfig : null,
                    customHeaders: propCustomHeaders
                };
                // Usar testWebhookByConfig com as configurações de auth do modal
                result = await WebhookService.testWebhookByConfig(testConfig, eventCode, payloadStr);
            } else if (webhookConfig) {
                // Mesclar auth config do modal com o webhookConfig
                const mergedConfig = {
                    ...webhookConfig,
                    authType: testAuthType,
                    authConfig: testAuthType !== 'NONE' ? testAuthConfig : null
                };
                result = await WebhookService.testWebhookByConfig(mergedConfig, eventCode, payloadStr);
            } else {
                setTestResults(prev => ({
                    ...prev,
                    [eventCode]: {
                        success: false,
                        errorMessage: intl.formatMessage({ id: 'webhook.test.no.config' })
                    }
                }));
                return false;
            }

            if (result.success) {
                setTestResults(prev => ({
                    ...prev,
                    [eventCode]: result.data
                }));
                return result.data.success;
            } else {
                setTestResults(prev => ({
                    ...prev,
                    [eventCode]: {
                        success: false,
                        errorMessage: result.errors ? result.errors[0] : intl.formatMessage({ id: 'process.error' })
                    }
                }));
                return false;
            }
        } catch (error) {
            setTestResults(prev => ({
                ...prev,
                [eventCode]: {
                    success: false,
                    errorMessage: error.message || intl.formatMessage({ id: 'process.error' })
                }
            }));
            return false;
        }
    };

    const testAllEvents = async () => {
        if (!selectedEvents || selectedEvents.length === 0) return;

        setTestingAll(true);
        setLoading(true);
        setProgress(0);

        const totalEvents = selectedEvents.length;
        let completed = 0;

        for (const event of selectedEvents) {
            const eventCode = event.eventCode;
            await testSingleEvent(eventCode);
            completed++;
            setProgress((completed / totalEvents) * 100);
        }

        setLoading(false);
        setTestingAll(false);
    };

    const getCardClass = (eventCode) => {
        const result = testResults[eventCode];
        if (!result) return classes.eventCard;
        if (result.loading) return `${classes.eventCard} ${classes.eventCardPending}`;
        if (getField(result, 'success')) return `${classes.eventCard} ${classes.eventCardSuccess}`;
        return `${classes.eventCard} ${classes.eventCardError}`;
    };

    const getResultChip = (eventCode) => {
        const result = testResults[eventCode];
        if (!result) return null;
        if (result.loading) {
            return <CircularProgress size={20} />;
        }
        const isSuccess = getField(result, 'success');
        const statusCode = getField(result, 'statusCode');
        const responseTimeMs = getField(result, 'responseTimeMs');
        return (
            <Chip
                size="small"
                icon={isSuccess ? <CheckCircleIcon /> : <ErrorIcon />}
                label={isSuccess
                    ? `${intl.formatMessage({ id: 'webhook.test.success' })} ${statusCode ? `(${statusCode})` : ''} ${responseTimeMs ? `- ${responseTimeMs}ms` : ''}`
                    : `${intl.formatMessage({ id: 'webhook.test.failed' })} ${statusCode ? `(${statusCode})` : ''}`}
                className={isSuccess ? classes.successChip : classes.errorChip}
            />
        );
    };

    return (
        <Modal
            aria-labelledby="webhook-test-modal-title"
            aria-describedby="webhook-test-modal-description"
            className={classes.modal}
            open={isOpen}
            onClose={handleClose}
            closeAfterTransition
            BackdropComponent={Backdrop}
            BackdropProps={{ timeout: 500 }}
        >
            <Fade in={isOpen}>
                <div className={classes.paper}>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="h6" id="webhook-test-modal-title">
                            {intl.formatMessage({ id: 'webhook.test.title' })} - {webhookName}
                        </Typography>
                        <IconButton
                            onClick={handleClose}
                            size="small"
                            aria-label="close"
                            sx={{
                                color: 'grey.500',
                                '&:hover': { color: 'grey.700' }
                            }}
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>

                    <Typography variant="body2" color="textSecondary" gutterBottom>
                        {intl.formatMessage({ id: 'webhook.test.description' })}
                    </Typography>

                    {/* Seção de Autenticação */}
                    <Accordion
                        expanded={authExpanded}
                        onChange={() => setAuthExpanded(!authExpanded)}
                        sx={{ mt: 2, mb: 2 }}
                    >
                        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                            <Box display="flex" alignItems="center" gap={1}>
                                <LockIcon fontSize="small" color={testAuthType !== 'NONE' ? 'primary' : 'disabled'} />
                                <Typography variant="subtitle2">
                                    {intl.formatMessage({ id: 'webhook.section.authentication' })}
                                </Typography>
                                {testAuthType !== 'NONE' && (
                                    <Chip
                                        label={AUTH_TYPES.find(t => t.value === testAuthType)?.value || testAuthType}
                                        size="small"
                                        color="primary"
                                        variant="outlined"
                                    />
                                )}
                                {propHasAuthConfig && testAuthType !== 'NONE' && Object.keys(testAuthConfig).length === 0 && (
                                    <Chip
                                        label={intl.formatMessage({ id: 'webhook.auth.fill.credentials' })}
                                        size="small"
                                        color="warning"
                                    />
                                )}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails>
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <FormControl fullWidth size="small" variant="outlined">
                                        <InputLabel>{intl.formatMessage({ id: 'webhook.auth.type' })}</InputLabel>
                                        <Select
                                            value={testAuthType}
                                            onChange={(e) => {
                                                setTestAuthType(e.target.value);
                                                setTestAuthConfig({});
                                            }}
                                            label={intl.formatMessage({ id: 'webhook.auth.type' })}
                                        >
                                            {AUTH_TYPES.map((type) => (
                                                <MenuItem key={type.value} value={type.value}>
                                                    {intl.formatMessage({ id: type.labelId })}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                {/* Campos de autenticação baseados no tipo */}
                                {testAuthType === 'BASIC' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                label={intl.formatMessage({ id: 'webhook.auth.username' })}
                                                value={testAuthConfig.username || ''}
                                                onChange={(e) => setTestAuthConfig(prev => ({ ...prev, username: e.target.value }))}
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                type={showPassword ? 'text' : 'password'}
                                                label={intl.formatMessage({ id: 'webhook.auth.password' })}
                                                value={testAuthConfig.password || ''}
                                                onChange={(e) => setTestAuthConfig(prev => ({ ...prev, password: e.target.value }))}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowPassword(!showPassword)}
                                                                edge="end"
                                                                size="small"
                                                            >
                                                                {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {testAuthType === 'BEARER' && (
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            size="small"
                                            variant="outlined"
                                            type={showToken ? 'text' : 'password'}
                                            label={intl.formatMessage({ id: 'webhook.auth.token' })}
                                            value={testAuthConfig.token || ''}
                                            onChange={(e) => setTestAuthConfig(prev => ({ ...prev, token: e.target.value }))}
                                            placeholder="eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9..."
                                            InputProps={{
                                                endAdornment: (
                                                    <InputAdornment position="end">
                                                        <IconButton
                                                            onClick={() => setShowToken(!showToken)}
                                                            edge="end"
                                                            size="small"
                                                        >
                                                            {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                        </IconButton>
                                                    </InputAdornment>
                                                )
                                            }}
                                        />
                                    </Grid>
                                )}

                                {testAuthType === 'API_KEY' && (
                                    <>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                label={intl.formatMessage({ id: 'webhook.auth.header.name' })}
                                                value={testAuthConfig.headerName || ''}
                                                onChange={(e) => setTestAuthConfig(prev => ({ ...prev, headerName: e.target.value }))}
                                                placeholder="X-API-Key"
                                            />
                                        </Grid>
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                fullWidth
                                                size="small"
                                                variant="outlined"
                                                type={showToken ? 'text' : 'password'}
                                                label={intl.formatMessage({ id: 'webhook.auth.api.key.value' })}
                                                value={testAuthConfig.apiKey || ''}
                                                onChange={(e) => setTestAuthConfig(prev => ({ ...prev, apiKey: e.target.value }))}
                                                InputProps={{
                                                    endAdornment: (
                                                        <InputAdornment position="end">
                                                            <IconButton
                                                                onClick={() => setShowToken(!showToken)}
                                                                edge="end"
                                                                size="small"
                                                            >
                                                                {showToken ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                                                            </IconButton>
                                                        </InputAdornment>
                                                    )
                                                }}
                                            />
                                        </Grid>
                                    </>
                                )}

                                {propHasAuthConfig && testAuthType !== 'NONE' && (
                                    <Grid item xs={12}>
                                        <Alert severity="info" sx={{ py: 0.5 }}>
                                            {testAuthType === 'BASIC' && intl.formatMessage({ id: 'webhook.test.auth.note.password' })}
                                            {testAuthType === 'BEARER' && intl.formatMessage({ id: 'webhook.test.auth.note.token' })}
                                            {testAuthType === 'API_KEY' && intl.formatMessage({ id: 'webhook.test.auth.note.apikey' })}
                                        </Alert>
                                    </Grid>
                                )}
                            </Grid>
                        </AccordionDetails>
                    </Accordion>

                    <Divider style={{ marginBottom: 16 }} />

                    {testingAll && (
                        <Box mb={2}>
                            <LinearProgress variant="determinate" value={progress} />
                            <Typography variant="caption" color="textSecondary">
                                {intl.formatMessage({ id: 'webhook.test.progress' })}: {Math.round(progress)}%
                            </Typography>
                        </Box>
                    )}

                    {/* Lista de eventos para testar */}
                    <Box style={{ maxHeight: '50vh', overflow: 'auto', marginBottom: 16 }}>
                        {selectedEvents && selectedEvents.length > 0 ? (
                            selectedEvents.map((event) => {
                                const eventCode = event.eventCode;
                                const description = event.description;
                                const result = testResults[eventCode];

                                // Obter o formato do payload da subscription
                                const subscription = getSubscriptionForEvent(event.id);
                                const payloadFormat = subscription?.payloadFormat || subscription?.payloadformat || 'STANDARD';
                                const formatLabels = {
                                    'STANDARD': { label: 'Padrão Mexx', color: '#1976d2' },
                                    'CUSTOM_JSON': { label: 'JSON Personalizado', color: '#388e3c' },
                                    'CUSTOM_FORM': { label: 'Form Data', color: '#f57c00' }
                                };
                                const formatInfo = formatLabels[payloadFormat] || formatLabels['STANDARD'];

                                return (
                                    <Card key={eventCode} className={getCardClass(eventCode)}>
                                        <CardContent style={{ paddingBottom: 8 }}>
                                            <Box display="flex" justifyContent="space-between" alignItems="center">
                                                <Box>
                                                    <Box display="flex" alignItems="center" gap={1} mb={0.5}>
                                                        <Typography variant="subtitle1" style={{ fontWeight: 500 }}>
                                                            {eventCode}
                                                        </Typography>
                                                        <Chip
                                                            label={formatInfo.label}
                                                            size="small"
                                                            sx={{
                                                                backgroundColor: formatInfo.color,
                                                                color: 'white',
                                                                height: 20,
                                                                fontSize: '10px',
                                                                fontWeight: 500
                                                            }}
                                                        />
                                                    </Box>
                                                    <Typography variant="caption" color="textSecondary">
                                                        {description}
                                                    </Typography>
                                                </Box>
                                                <Box display="flex" alignItems="center" gap={1}>
                                                    {getResultChip(eventCode)}
                                                    <IconButton
                                                        size="small"
                                                        onClick={() => toggleExpand(eventCode)}
                                                    >
                                                        {expandedEvents[eventCode] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    </IconButton>
                                                </Box>
                                            </Box>

                                            <Collapse in={expandedEvents[eventCode]}>
                                                <Box mt={2}>
                                                    <JsonEditor
                                                        value={eventPayloads[eventCode] || ''}
                                                        onChange={(newValue) => handlePayloadChange(eventCode, newValue)}
                                                        minHeight={150}
                                                        maxHeight={500}
                                                    />

                                                    {result && getField(result, 'errorMessage') && (
                                                        <Alert severity="error" style={{ marginTop: 8 }}>
                                                            {getField(result, 'errorMessage')}
                                                        </Alert>
                                                    )}

                                                    {/* Status Code */}
                                                    {result && !result.loading && getField(result, 'statusCode') && (
                                                        <Box mt={1}>
                                                            <Typography variant="caption" color="textSecondary">
                                                                Status Code:
                                                            </Typography>
                                                            <div className={classes.resultBox}>
                                                                <pre style={{ margin: 0 }}>{getField(result, 'statusCode')}</pre>
                                                            </div>
                                                        </Box>
                                                    )}

                                                    {/* Response Headers */}
                                                    {result && !result.loading && (() => {
                                                        const headers = getField(result, 'responseHeaders');
                                                        const headersText = headers ? Object.entries(headers).map(([key, value]) =>
                                                            `${key}: ${value}`
                                                        ).join('\n') : '';
                                                        return headers && Object.keys(headers).length > 0 && (
                                                            <Box mt={1}>
                                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        Response Headers:
                                                                    </Typography>
                                                                    <Tooltip title={intl.formatMessage({ id: 'copy' })}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => navigator.clipboard && navigator.clipboard.writeText(headersText)}
                                                                            style={{ padding: 2 }}
                                                                        >
                                                                            <ContentCopyIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                                <div className={classes.resultBox}>
                                                                    <pre style={{ margin: 0 }}>{headersText}</pre>
                                                                </div>
                                                            </Box>
                                                        );
                                                    })()}

                                                    {/* Response Body */}
                                                    {result && !result.loading && (() => {
                                                        const body = getField(result, 'responseBody');
                                                        const bodyText = typeof body === 'string' ? body : JSON.stringify(body, null, 2);
                                                        return body && (
                                                            <Box mt={1}>
                                                                <Box display="flex" alignItems="center" justifyContent="space-between">
                                                                    <Typography variant="caption" color="textSecondary">
                                                                        Response Body:
                                                                    </Typography>
                                                                    <Tooltip title={intl.formatMessage({ id: 'copy' })}>
                                                                        <IconButton
                                                                            size="small"
                                                                            onClick={() => navigator.clipboard && navigator.clipboard.writeText(bodyText)}
                                                                            style={{ padding: 2 }}
                                                                        >
                                                                            <ContentCopyIcon fontSize="small" />
                                                                        </IconButton>
                                                                    </Tooltip>
                                                                </Box>
                                                                <div className={classes.resultBox}>
                                                                    <pre style={{ margin: 0 }}>{bodyText}</pre>
                                                                </div>
                                                            </Box>
                                                        );
                                                    })()}
                                                </Box>
                                            </Collapse>
                                        </CardContent>
                                        <CardActions style={{ paddingTop: 0 }}>
                                            <Button
                                                size="small"
                                                startIcon={result?.loading ? <CircularProgress size={16} /> : <PlayArrowIcon />}
                                                onClick={() => testSingleEvent(eventCode)}
                                                disabled={loading || result?.loading}
                                            >
                                                {intl.formatMessage({ id: 'webhook.test.single' })}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                );
                            })
                        ) : (
                            <Alert severity="warning">
                                {intl.formatMessage({ id: 'webhook.test.no.events' })}
                            </Alert>
                        )}
                    </Box>

                    <Divider style={{ marginBottom: 16 }} />

                    {/* Botões de ação */}
                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6}>
                            <Button onClick={handleClose} variant="outlined" fullWidth>
                                {intl.formatMessage({ id: 'close' })}
                            </Button>
                        </Grid>
                        <Grid item xs={12} sm={6}>
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={testAllEvents}
                                disabled={loading || !selectedEvents || selectedEvents.length === 0}
                                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                                fullWidth
                            >
                                {loading
                                    ? intl.formatMessage({ id: 'webhook.test.sending.all' })
                                    : intl.formatMessage({ id: 'webhook.test.send.all' })}
                            </Button>
                        </Grid>
                    </Grid>
                </div>
            </Fade>
        </Modal>
    );
}

export default injectIntl(WebhookTestModal);
